import React, { useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, TextField, Checkbox, FormControlLabel } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import useVerticals from 'data_sources/useVerticals';
import { createAccreditationVersion } from '../../../_data_sources/AccreditationVersions/manageAccreditationVersions';
import { type ServerErrorResponse } from 'data_sources/constants';
import Modal from 'components/_layouts/Modal';
import errorHelper from 'helpers/errorHelper';
import { AccreditationBody, AccreditationVersion } from '../../../_types/awb';
import JurisdictionPicker from 'components/_pickers/JurisdictionPicker';
import VerticalPicker from 'components/_pickers/VerticalPicker';

export type NewAccreditingBodyProps = {
  accreditingBody: AccreditationBody;
  setLastId: React.Dispatch<React.SetStateAction<string | null>>;
};

type FormValues = {
  version: string;
  abbreviation: string;
  jurisdictions: string[];
  verticalId: string;
  nationwide: boolean;
};

const NewAccreditingBody: React.FC<NewAccreditingBodyProps> = ({
  accreditingBody,
  setLastId,
}: NewAccreditingBodyProps) => {
  const queryClient = useQueryClient();
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const verticalsQuery = useVerticals();

  const accreditingBodyVersionschema = Yup.object().shape({
    version: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('awb.accreditingBodyVersions.properties.version')} is required`),
    abbreviation: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('awb.accreditingBodyVersions.properties.abbreviation')} is required`),
    jurisdictions: Yup.array()
      .of(Yup.string().required())
      .transform((value, originalValue) => {
        return originalValue.filter((x: string) => x.length > 0);
      })
      .test('jurisdictions', 'At least one jurisdiction is required', function (value) {
        return this.parent.nationwide || (value && value.length > 0);
      }),
    verticalId: Yup.string().required(`${t('awb.accreditingBodyVersions.properties.vertical')} is required`),
    nationwide: Yup.boolean().default(false),
  }) as Yup.ObjectSchema<FormValues>;

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      version: '',
      abbreviation: '',
      jurisdictions: [],
      nationwide: false,
      verticalId: '',
    },
    resolver: yupResolver(accreditingBodyVersionschema),
  });

  const nationwide = watch('nationwide');

  const handleSave: SubmitHandler<FormValues> = (values: FormValues) => {
    setIsSaving(true);
    const dataToSend: AccreditationVersion = {
      ...values,
      jurisdictions: values.nationwide ? [] : values.jurisdictions,
    };
    createAccreditationVersion(
      accreditingBody.id!,
      dataToSend,
      (data) => {
        reset();
        setServerErrors(undefined);
        queryClient.invalidateQueries(['accreditation_versions']);
        if (data.id) setLastId(data.id);

        setIsSaving(false);
        setShowNewModal(false);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setServerErrors(errors);
      }
    );
  };

  return (
    <>
      <Button
        onClick={() => setShowNewModal(true)}
        size='small'
      >
        {t('awb.accreditingBodyVersions.actions.create')}
      </Button>
      <Modal
        title={`${t('awb.accreditingBodyVersions.new.title')} - ${accreditingBody.abbreviation}`}
        onSubmit={handleSubmit(handleSave)}
        size='md'
        actions={
          <>
            <Button
              color='neutral'
              onClick={() => setShowNewModal(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                trigger();
              }}
              disabled={isSaving}
              component='button'
              type='submit'
              variant='contained'
            >
              {t('common.save')}
            </Button>
          </>
        }
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
      >
        {errorHelper.renderAllErrors(serverErrors, errors)}
        <TextField
          id='version'
          key='version'
          label={t('awb.accreditingBodyVersions.properties.version')}
          variant='outlined'
          required
          fullWidth
          {...register('version')}
          error={!!errors.version}
          helperText={errors.version ? errors.version.message : undefined}
        />
        <TextField
          id='abbreviation'
          key='abbreviation'
          label={t('awb.accreditingBodyVersions.properties.abbreviation')}
          variant='outlined'
          required
          fullWidth
          {...register('abbreviation')}
          error={!!errors.abbreviation}
          helperText={errors.abbreviation ? errors.abbreviation.message : undefined}
        />
        <Controller
          name='jurisdictions'
          control={control}
          render={({ field }) => (
            <JurisdictionPicker
              allowMultiple
              id='jurisdictions'
              {...field}
              label={t('awb.accreditingBodyVersions.properties.jurisdictions')}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
              }}
              required={!nationwide}
              disabled={nationwide}
              error={!!errors.jurisdictions}
              helperText={errors.jurisdictions ? errors.jurisdictions.message : undefined}
            />
          )}
        />
        <FormControlLabel
          control={
            <Controller
              name='nationwide'
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    if (e.target.checked) {
                      setValue('jurisdictions', []);
                    }
                  }}
                />
              )}
            />
          }
          label={t('awb.accreditingBodyVersions.properties.nationwide')}
        />
        <Controller
          name='verticalId'
          control={control}
          render={({ field }) => (
            <VerticalPicker
              id='verticalId'
              {...field}
              label={t('awb.accreditingBodyVersions.properties.vertical')}
              value={!!field.value ? [field.value] : []}
              onChange={(value) => {
                field.onChange(value[0]);
              }}
              required
              error={!!errors.verticalId}
              helperText={errors.verticalId ? errors.verticalId.message : undefined}
            />
          )}
        />
      </Modal>
    </>
  );
};

export default NewAccreditingBody;
